import React, { useEffect, useState } from 'react';
import { Layout } from "@/components/Layout/Layout";
import { MainHeading } from "@/components/common/MainHeading";
import { Plus, Pencil, Trash2, Download } from "lucide-react";
import { callApi, ThrowError } from "@/api/callApi";
import { API_ENDPOINT, API_URL } from "@/api/constant";
import CommonTable, { TableColumn, Action } from "@/components/common/CommonTable";
import { StatusBadge } from "@/components/common/StatusBadge";
import { ITag, ITagResponse } from "@/interfaces/Tag/tag.interface";
import { AddTag } from "./AddTag";
import { GetFilterUrl, showToastNotification, downloadExcel } from "@/utility/helper";

import { IFilter, IPageInfo } from '@/interfaces/common.interface';
import { useFilter } from '@/hooks/useFilter';
import Pagination from '@/components/common/Pagination';
import Button from "@/components/common/Button";
import DateRangePicker from "@/components/common/DateRange";
import { Range } from "react-date-range";
import { format } from "date-fns";
import { TableFilters } from '@/components/common/TableFilters';
import { CommonSelect } from '@/components/common/CommonSelect';
import ConfirmationModal from '@/components/common/ConfirmationModal';
import { Switch } from '@/components/ui/switch';
import { usePermissions } from '@/hooks/usePermissions';
import { RESOURCES } from '@/context/permissions';

export const Tags = () => {
    const [tags, setTags] = useState<ITag[]>([]);
    const [loading, setLoading] = useState(false);
    const [showAddModal, setShowAddModal] = useState(false);
    const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
    const [selectedTag, setSelectedTag] = useState<ITag | null>(null);
    const [togglingId, setTogglingId] = useState<string | null>(null);

    const { canView, canCreate, canEdit, canDelete, canExport } = usePermissions();

    // Deletion State
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [tagToDelete, setTagToDelete] = useState<ITag | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const { filters, debouncedFilters, updateFilter, handlePageChange } = useFilter<IFilter>({
        search: "",
        status: "",
        page: 1,
        limit: 10,
        startDate: '',
        endDate: '',
    });

    const [loadingExport, setLoadingExport] = useState<boolean>(false);

    const handleExport = async () => {
        const queryParams = GetFilterUrl({
            status: filters.status,
            startDate: filters.startDate,
            endDate: filters.endDate,
            sort: filters.sort
        });
        const url = `${API_URL + API_ENDPOINT.tags}/export/excel?${queryParams}`;
        setLoadingExport(true);
        try {
            await downloadExcel(url, 'tags_export.xlsx');
        } catch (error) {
            ThrowError(error);
        } finally {
            setLoadingExport(false);
        }
    };

    const [dateRange, setDateRange] = useState<Range[]>([
        {
            startDate: undefined,
            endDate: undefined,
            key: 'selection',
        },
    ]);

    const handleDateChange = (newRange: any) => {
        const resolvedRange = typeof newRange === 'function' ? newRange(dateRange) : newRange;
        setDateRange(resolvedRange);

        const start = resolvedRange[0]?.startDate;
        const end = resolvedRange[0]?.endDate;
        const formattedStart = start ? format(start, 'yyyy-MM-dd') : '';
        const formattedEnd = end ? format(end, 'yyyy-MM-dd') : '';

        if (formattedStart !== filters.startDate || formattedEnd !== filters.endDate) {
            updateFilter({ startDate: formattedStart, endDate: formattedEnd, page: 1 });
        }
    };

    const [pageMetadata, setPageMetadata] = useState<IPageInfo>({
        limit: 10,
        page: 1,
        total: 0,
        totalPages: 0
    });

    const fetchTags = async () => {
        setLoading(true);
        try {
            const queryParams = GetFilterUrl(debouncedFilters);

            const response = await callApi<ITagResponse>(
                `${API_URL + API_ENDPOINT.tags}?${queryParams.toString()}`,
                { method: 'GET' }
            );
            setTags(response?.data?.tags || []);
            setPageMetadata(
                response?.data?.pageInfo || { limit: 10, page: 1, total: 0, totalPages: 0 }
            );
        } catch (error) {
            ThrowError(error);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteTag = async () => {
        if (!tagToDelete) return;
        setIsDeleting(true);
        try {
            await callApi(`${API_URL + API_ENDPOINT.tags}/${tagToDelete.id}`, {
                method: 'DELETE'
            });
            showToastNotification('success', 'Tag deleted successfully');
            setShowDeleteModal(false);
            fetchTags();
        } catch (error) {
            ThrowError(error);
        } finally {
            setIsDeleting(false);
            setTagToDelete(null);
        }
    };

    const handleToggleStatus = async (item: ITag) => {
        setTogglingId(item.id);
        const newStatus = item.status === "active" ? "inactive" : "active";
        try {
            await callApi(`${API_URL + API_ENDPOINT.tags}/${item.id}`, {
                method: "PATCH",
                data: {
                    status: newStatus
                }
            });
            showToastNotification("success", "Tag status updated successfully");
            setTags(tags.map(tag => tag.id === item.id ? { ...tag, status: newStatus } : tag));
        } catch (error) {
            ThrowError(error);
        } finally {
            setTogglingId(null);
        }
    };

    useEffect(() => {
        fetchTags();
    }, [debouncedFilters.search, debouncedFilters.status, debouncedFilters.page, debouncedFilters.limit, debouncedFilters.startDate, debouncedFilters.endDate]);

    const columns: TableColumn<ITag>[] = [
        {
            key: 'name',
            header: 'Tag Name',
            render: (item) => (
                <div className="flex flex-col">
                    <span className="font-bold text-foreground">{item.name}</span>
                </div>
            )
        },
        {
            key: 'status',
            header: 'Status',
            render: (item) => (
                <div className="flex items-center gap-3">
                    <Switch
                        checked={item.status === "active"}
                        onCheckedChange={() => handleToggleStatus(item)}
                        disabled={togglingId === item.id || !canEdit(RESOURCES.TAG)}
                        className="data-[state=checked]:bg-green-500"
                    />
                    <StatusBadge status={item.status} />
                </div>
            )
        },
        {
            key: 'createdAt',
            header: 'Created Date',
            render: (item) => (
                <span className="text-xs text-muted-foreground">
                    {new Date(item.createdAt).toLocaleDateString()}
                </span>
            )
        }
    ];

    return (
        <Layout>
            <MainHeading
                title="Tag Management"
                description="Create and manage product tags for better categorization"
                actionLabel={canCreate(RESOURCES.TAG) ? "Create Tag" : undefined}
                actionIcon={canCreate(RESOURCES.TAG) ? <Plus size={16} /> : undefined}
                onAction={() => {
                    setSelectedTag(null);
                    setModalMode('create');
                    setShowAddModal(true);
                }}
            />

            <div className="mt-6 space-y-2">
                <TableFilters
                    searchTerm={filters.search}
                    onSearchChange={(val) => updateFilter({ search: val })}
                    placeholder="Search tags..."
                >
                    <div className="flex items-center gap-3 flex-wrap">
                        <DateRangePicker
                            dateRange={dateRange}
                            setDateRange={handleDateChange as any}
                        />
                        <CommonSelect
                            value={filters.status || ""}
                            onValueChange={(val) => updateFilter({ status: val })}
                            options={[
                                // { label: "All Status", value: "" },
                                { label: "Active", value: "active" },
                                { label: "Inactive", value: "inactive" }
                            ]}
                            triggerClassName="w-[180px]"
                            placeholder="Select Status"
                        />
                        {canExport(RESOURCES.TAG) && <Button
                            variant="outline"
                            onClick={() => handleExport()}
                            className="h-10 px-4 flex items-center gap-2 rounded-xl"
                            leftIcon={<Download className="w-4 h-4" />}
                            isLoading={loadingExport}
                        >
                            Export
                        </Button>}
                    </div>
                </TableFilters>

                <CommonTable
                    columns={columns}
                    data={tags}
                    loading={loading}
                    actions={(tag) => {
                        const availableActions: Action<ITag>[] = [];
                        if (canEdit(RESOURCES.TAG)) {
                            availableActions.push({
                                text: 'Edit',
                                icon: <Pencil size={16} className=" cursor-pointer" />,
                                onClick: (t) => {
                                    setSelectedTag(t);
                                    setModalMode('edit');
                                    setShowAddModal(true);
                                }
                            });
                        }
                        if (canDelete(RESOURCES.TAG)) {
                            availableActions.push({
                                text: 'Delete',
                                icon: <Trash2 size={16} className="text-destructive cursor-pointer" />,
                                onClick: (t) => {
                                    setTagToDelete(t);
                                    setShowDeleteModal(true);
                                }
                            });
                        }
                        return availableActions;
                    }}
                />

                <Pagination
                    currentPage={pageMetadata.page}
                    totalPages={pageMetadata.totalPages}
                    totalItems={pageMetadata.total}
                    itemsPerPage={pageMetadata.limit}
                    onPageChange={handlePageChange}
                />
            </div>

            <AddTag
                showAddModal={showAddModal}
                setShowAddModal={setShowAddModal}
                modalMode={modalMode}
                selectedTag={selectedTag}
                onSuccess={fetchTags}
            />

            <ConfirmationModal
                open={showDeleteModal}
                onOpenChange={setShowDeleteModal}
                title="Delete Tag"
                message={`Are you sure you want to delete the tag "${tagToDelete?.name}"? This action cannot be undone.`}
                confirmText="Delete"
                onConfirm={handleDeleteTag}
                loading={isDeleting}
                size='lg'
            />
        </Layout>
    );
};
